/**
 * snoozeHold — a notification stops being able to disappear on its own the
 * moment its snooze menu opens.  (notify-center, 7 Sep 2026)
 *
 * Ruhshad: "On clicking snooze it shows the options but then after a couple of
 * seconds the notification box disappears. On clicking snooze it should remain
 * open till something is selected."
 *
 * The mail toast is not a sonner toast — components/mail/MailNotifyBridge.jsx
 * arms its own `setTimeout(() => dismiss(n.id), 12000)` in `announce()`.  That
 * timer knew nothing about the menu, so it fired mid-decision and took the card
 * away.  Two more ways the same card could vanish: the 30 s poll pushes a new
 * batch on top and `.slice(0, 5)` drops it, and the Header's list refresh
 * re-slices to 15 and can drop the row underneath an open menu.
 *
 * This module owns every auto-dismiss timer for a notification id, so all three
 * routes are closed in one place:
 *
 *   scheduleDismiss(id, ms, fn)  arm the auto-dismiss (no-op while held)
 *   holdToast(id)                menu opened  -> cancel the timer
 *   releaseToast(id, opts)       menu closed  -> re-arm it
 *                                  { dismiss: true }   close now
 *                                  { restartMs: 3000 } short window so a
 *                                                      confirmation is readable
 *                                  (nothing)           restore the full timer
 *   keepHeld(prev, next, cap)    never let a slice drop a held row
 *
 * Held ids are module state, shared by every mount, so the toast card and the
 * Header row of the same notification cannot fight each other.
 */

const HOLD_EVENT = 'dcs:snooze-hold';

const held = new Set();
/** id -> { ms, fn, handle } */
const timers = new Map();

const key = (id) => String(id);

function emit() {
  try {
    window.dispatchEvent(new CustomEvent(HOLD_EVENT, { detail: { held: [...held] } }));
  } catch (e) { /* no window, or a browser without CustomEvent */ }
}

/* ------------------------------------------------------------------ */
/* state                                                               */
/* ------------------------------------------------------------------ */

export const isHeld = (id) => held.has(key(id));
export const anyHeld = () => held.size > 0;
export const heldIds = () => [...held];

/** Subscribe to hold/release; returns an unsubscribe. */
export function onHoldChange(fn) {
  const handler = (e) => fn((e && e.detail && e.detail.held) || []);
  try {
    window.addEventListener(HOLD_EVENT, handler);
    return () => window.removeEventListener(HOLD_EVENT, handler);
  } catch (e) {
    return () => {};
  }
}

/* ------------------------------------------------------------------ */
/* timers                                                              */
/* ------------------------------------------------------------------ */

function start(id) {
  const t = timers.get(id);
  if (!t || t.handle || held.has(id)) return;
  t.handle = setTimeout(() => {
    timers.delete(id);
    held.delete(id);
    try { t.fn(); } catch (e) { /* the host went away */ }
  }, t.ms);
  if (t.handle && typeof t.handle === 'object' && t.handle.unref) t.handle.unref();
}

function stop(id) {
  const t = timers.get(id);
  if (t && t.handle) {
    clearTimeout(t.handle);
    t.handle = null;
  }
}

/** Arm (or re-arm) the auto-dismiss for one notification. */
export function scheduleDismiss(id, ms, fn) {
  const k = key(id);
  stop(k);
  timers.set(k, { ms, fn, handle: null });
  start(k);            // start() itself refuses while the id is held
  return k;
}

/** Drop the timer entirely (the card is going away now). */
export function cancelDismiss(id) {
  const k = key(id);
  stop(k);
  timers.delete(k);
}

/** True while a timer is armed and counting — used by the tests. */
export const isTicking = (id) => {
  const t = timers.get(key(id));
  return !!(t && t.handle);
};

/** Menu opened: this notification can no longer disappear on its own. */
export function holdToast(id) {
  const k = key(id);
  stop(k);
  held.add(k);
  emit();
}

/**
 * Menu closed.
 *   { dismiss: true }  -> drop the card now
 *   { restartMs: n }   -> re-arm with n ms (a readable confirmation window)
 *   {}                 -> restore the original timer, exactly as it was
 */
export function releaseToast(id, options) {
  const k = key(id);
  const opts = options || {};
  held.delete(k);
  emit();
  if (opts.dismiss) {
    const t = timers.get(k);
    cancelDismiss(k);
    if (t && typeof t.fn === 'function') {
      try { t.fn(); } catch (e) { /* host gone */ }
    }
    return;
  }
  const t = timers.get(k);
  if (t && typeof opts.restartMs === 'number' && opts.restartMs > 0) t.ms = opts.restartMs;
  start(k);
}

/** Panic button — used when a whole surface unmounts. */
export function releaseAll() {
  held.clear();
  emit();
  [...timers.keys()].forEach(start);
}

/** Test/teardown helper: forget everything without firing anything. */
export function resetHolds() {
  [...timers.keys()].forEach(stop);
  timers.clear();
  held.clear();
  emit();
}

/* ------------------------------------------------------------------ */
/* list helpers                                                        */
/* ------------------------------------------------------------------ */

/**
 * Merge a freshly built list with the previous one so that a row whose snooze
 * menu is open is never dropped — not by a new batch arriving on the 30 s
 * poll, and not by the `.slice(cap)` that keeps the stack short.
 * Identity is preserved: the SAME object reference is carried over, so React
 * does not re-key or remount the row underneath an open menu.
 */
export function keepHeld(prev, next, cap) {
  const list = Array.isArray(next) ? next.slice() : [];
  const previous = Array.isArray(prev) ? prev : [];
  const present = new Set(list.map((r) => r && key(r.id)));

  previous.forEach((row) => {
    if (!row || !row.id) return;
    const k = key(row.id);
    if (held.has(k) && !present.has(k)) {
      list.unshift(row);          // the same object, not a copy
      present.add(k);
    }
  });

  if (typeof cap !== 'number' || list.length <= cap) return list;

  // Trim to the cap, but a held row is never what gets trimmed.
  const keepers = list.filter((r) => r && held.has(key(r.id)));
  const rest = list.filter((r) => !(r && held.has(key(r.id))));
  const room = Math.max(0, cap - keepers.length);
  const trimmed = rest.slice(0, room);
  // Put everything back in its original order.
  const allowed = new Set(keepers.concat(trimmed));
  return list.filter((r) => allowed.has(r));
}

/* ------------------------------------------------------------------ */
/* confirmation wording                                                */
/* ------------------------------------------------------------------ */

/**
 * "Snoozed until 4:30 pm" / "Snoozed until tomorrow 9:00 am".
 * Driven by the timestamp the backend returns, so it says what really happened
 * rather than echoing the button that was pressed.
 */
export function formatSnoozeUntil(iso, now) {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const today = now instanceof Date ? now : new Date();

  let time;
  try {
    time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      .replace(/\s?([AP])M/i, (m, p) => ` ${p.toLowerCase()}m`);
  } catch (e) {
    time = at.toISOString().slice(11, 16);
  }

  const dayDiff = Math.round(
    (new Date(at.getFullYear(), at.getMonth(), at.getDate()) -
     new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000,
  );
  if (dayDiff <= 0) return `Snoozed until ${time}`;
  if (dayDiff === 1) return `Snoozed until tomorrow ${time}`;
  let day;
  try {
    day = at.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  } catch (e) {
    day = at.toISOString().slice(0, 10);
  }
  return `Snoozed until ${day}, ${time}`;
}

export default {
  anyHeld,
  cancelDismiss,
  formatSnoozeUntil,
  heldIds,
  holdToast,
  isHeld,
  isTicking,
  keepHeld,
  onHoldChange,
  releaseAll,
  releaseToast,
  resetHolds,
  scheduleDismiss,
};
